import { useMemo } from 'react';
import * as R from 'remeda';
import { produce } from 'immer';
import { ExtensionEventAction } from '@rossum/api-client/hooks';
import { useUnpaginatedHooks } from '../../../../hooks/useUnpaginatedHooks';
import { normalizedEventsWithoutInvocation } from '../../../../lib/helpers';
import {
  createGraphFunctionsInput,
  ExtensionsGraphData,
  ExtensionsRelationMap,
  getGraphData,
  getQueueHooksQuery,
  mergeParentsAndChildren,
  normalizeEventsInExtensions,
} from '../helpers';

type UseGraphHooks = {
  queueId?: number;
  enabled?: boolean;
};

export type GraphDataGroupedPerEvents = Array<{
  eventNames: ExtensionEventAction[];
  graphData: ExtensionsGraphData;
  extensionGraphNodesMap: ExtensionsRelationMap;
}>;

export const useGraphHooks = ({ queueId, enabled }: UseGraphHooks) => {
  const {
    data: extensions,
    isLoading,
    isFetching,
  } = useUnpaginatedHooks(getQueueHooksQuery(queueId), {
    cacheTime: 0, // removes the cache on unmounting component
    enabled,
  });

  const graphDataGroupedPerEvents = useMemo(() => {
    const withNormalizedEvents = normalizeEventsInExtensions(extensions ?? []);

    const graphDataForAllEvents = normalizedEventsWithoutInvocation.map(
      event => {
        const graphFunctionsInput = createGraphFunctionsInput(
          withNormalizedEvents,
          event
        );

        return {
          eventNames: [event],
          graphData: getGraphData(graphFunctionsInput),
          extensionGraphNodesMap: mergeParentsAndChildren(graphFunctionsInput),
        };
      }
    );

    return graphDataForAllEvents.reduce<GraphDataGroupedPerEvents>(
      (acc, event) => {
        const equalDataIndex = acc.findIndex(accEvent =>
          R.isDeepEqual(accEvent.graphData, event.graphData)
        );

        if (equalDataIndex === -1) {
          return [...acc, event];
        }

        return produce(acc, draft => {
          draft[equalDataIndex]?.eventNames.push(...event.eventNames);
        });
      },
      []
    );
  }, [extensions]);

  return {
    graphDataGroupedPerEvents,
    isLoading,
    isFetching,
    originalExtensions: extensions,
  };
};
